import type { AxiosProgressEvent } from 'axios';
import api from '@/shared/api';
import { buildFormData, parseContentDispositionFilename } from '@/shared/lib';
import type { MemberTrainingStatus } from '../model/types';
import type {
  GenerateCollaboratorCertificateResponse,
  GenerateCollaboratorCertificatesBatchResponse,
  PaginatedTrainingCollaboratorsResponse,
  TrainingCollaboratorListItem,
  TrainingCollaboratorSearchType,
  TrainingCollaboratorSortField,
  TrainingCollaboratorSortOrder,
  UpdateTrainingCollaboratorStatusResponse,
  UploadCollaboratorCertificateResponse,
} from '../model/trainingCollaborator.types';

export interface UploadCollaboratorCertificateOptions {
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

export interface CollaboratorCertificateFile {
  blob: Blob;
  filename: string | null;
}

export interface ListTrainingCollaboratorsParams {
  page?: number;
  itemsPerPage?: number;
  term?: string;
  searchType?: TrainingCollaboratorSearchType;
  sort?: TrainingCollaboratorSortField;
  order?: TrainingCollaboratorSortOrder;
  status?: MemberTrainingStatus;
  positionId?: number;
}

class TrainingCollaboratorApi {
  async listTrainingCollaborators(
    trainingId: number,
    params: ListTrainingCollaboratorsParams = {}
  ): Promise<PaginatedTrainingCollaboratorsResponse> {
    const response = await api.get<PaginatedTrainingCollaboratorsResponse>(
      `/trainings/${trainingId}/collaborators`,
      { params }
    );
    return response.data;
  }

  async updateCollaboratorStatus(
    trainingId: number,
    collaboratorId: number,
    status: MemberTrainingStatus
  ): Promise<TrainingCollaboratorListItem> {
    const response = await api.patch<UpdateTrainingCollaboratorStatusResponse>(
      `/trainings/${trainingId}/collaborators/${collaboratorId}/status`,
      { status }
    );
    return response.data.data;
  }

  async getCollaboratorCertificate(
    trainingId: number,
    memberTrainingId: number
  ): Promise<CollaboratorCertificateFile> {
    const response = await api.get<Blob>(
      `/trainings/${trainingId}/collaborators/${memberTrainingId}/certificate`,
      { responseType: 'blob' }
    );

    return {
      blob: response.data,
      filename: parseContentDispositionFilename(
        response.headers['content-disposition'] as string | undefined
      ),
    };
  }

  async generateCollaboratorCertificate(
    trainingId: number,
    memberTrainingId: number
  ): Promise<GenerateCollaboratorCertificateResponse> {
    const response = await api.post<GenerateCollaboratorCertificateResponse>(
      `/trainings/${trainingId}/collaborators/${memberTrainingId}/certificate/generate`
    );
    return response.data;
  }

  async generateTrainingCertificatesBatch(
    trainingId: number
  ): Promise<GenerateCollaboratorCertificatesBatchResponse> {
    const response = await api.post<GenerateCollaboratorCertificatesBatchResponse>(
      `/trainings/${trainingId}/certificates/generate`
    );
    return response.data;
  }

  async uploadCollaboratorCertificate(
    trainingId: number,
    memberTrainingId: number,
    file: File,
    options: UploadCollaboratorCertificateOptions = {}
  ): Promise<UploadCollaboratorCertificateResponse> {
    const response = await api.post<UploadCollaboratorCertificateResponse>(
      `/trainings/${trainingId}/collaborators/${memberTrainingId}/certificate`,
      buildFormData({ file }),
      {
        headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' },
        onUploadProgress: options.onUploadProgress,
        signal: options.signal,
      }
    );
    return response.data;
  }
}

export const trainingCollaboratorApi = new TrainingCollaboratorApi();
